import { useLocation, useNavigate, useSearchParams } from '@solidjs/router';
import { createEffect } from 'solid-js';
import {
  onboardingAIChatMapping,
  BusinessInfomationStep,
  routes,
  STATUS_PROGRESS_MAP,
  VERIFICATION_STEPS,
} from '~/components/Onboarding/V2/config';
import { useOnboardingAi, useOnboarding } from '~/contexts/global';
import { OnboardingStatus } from '~/utils/constant';
import type { CompanyStatusDto } from '~/swagger/Api';

export const PHONE_SKIP_STORAGE_KEY = 'onboarding_phone_skipped';

export const isPhoneStepSkipped = () => {
  return localStorage.getItem(PHONE_SKIP_STORAGE_KEY) === 'true';
};

export const getCurrentProgress = (
  status: OnboardingStatus | undefined,
  isStepCompleted: (step: keyof CompanyStatusDto) => any
): number => {
  if (!status) return STATUS_PROGRESS_MAP.DEFAULT;

  const statusProgress = STATUS_PROGRESS_MAP[status];

  if (status === OnboardingStatus.GET_VERIFIED_CHECKLIST) {
    for (const step of VERIFICATION_STEPS) {
      if (!isStepCompleted(step.statusKey)) {
        return step.progress;
      }
    }
    return VERIFICATION_STEPS.at(-1)?.progress ?? 0;
  }

  return statusProgress;
};

export const useInjection = (key: keyof typeof onboardingAIChatMapping) => {
  const { injectMessage, currentChatId, isInitialized, chatHistory, setCurrentInjectionKey } = useOnboardingAi();
  createEffect(() => {
    const _injectionKey = onboardingAIChatMapping[key];
    const _currentChatId = currentChatId();
    const _isInitialized = isInitialized();
    const _chatHistory = chatHistory();

    setCurrentInjectionKey(_injectionKey);

    if (_currentChatId && _isInitialized && !injectMessage.loading) {
      const isInjected = _chatHistory?.messages.some((message) => message.injectionKey === _injectionKey);
      if (!isInjected) {
        injectMessage({ key: _injectionKey });
      } else {
        console.warn(`${_injectionKey} is already injected`);
      }
    }
  });
};

export const useAutoNavigationToStep = () => {
  const { companyStatus, companies, isStepCompleted } = useOnboarding();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams<{ doNotRedirect?: string }>();

  const navigationToStep = () => {
    if (companyStatus.loading || !companyStatus() || companies.loading) return;

    const status = companies()?.onboardingStatus;

    if (status === OnboardingStatus.USER_HIDE_ONBOARDING) {
      return navigate(routes.goLive);
    }

    if (status === OnboardingStatus.PAID) {
      return navigate(routes.goLive);
    }

    if (status === OnboardingStatus.IMPORTING) {
      return navigate(routes.importing);
    }

    if (status === OnboardingStatus.GET_VERIFIED_CHECKLIST) {
      for (const step of VERIFICATION_STEPS) {
        if (step.statusKey === 'phoneNumberSettled' && isPhoneStepSkipped()) {
          continue;
        }

        if (!isStepCompleted(step.statusKey)) {
          const route = typeof step.route === 'function' ? step.route() : step.route;
          return navigate(route);
        }
      }
    }
    return navigate(`${routes.businessInfo}?step=${BusinessInfomationStep.COMPANY_ADDRESS}`);
  };

  createEffect(() => {
    location.pathname;
    if (searchParams.doNotRedirect) return;
    navigationToStep();
  });
};
